import { clamp } from '../util/math.js';

let wheel = null;

export function initWeaponWheel(options = {}) {
  wheel = {
    options,
    root: createWheel(),
    selectedIndex: 0,
    visible: false,
    mouseAngle: -Math.PI / 2,
  };
  window.addEventListener('mousemove', (event) => {
    if (!wheel.visible) return;
    const cx = window.innerWidth / 2;
    const cy = window.innerHeight / 2;
    updateWeaponWheel(Math.atan2(event.clientY - cy, event.clientX - cx));
  });
  return wheel;
}

export function showWeaponWheel() {
  if (!wheel) initWeaponWheel();
  const selected = getSlots().findIndex((slot) => slot.selected);
  if (selected >= 0) wheel.selectedIndex = selected;
  wheel.visible = true;
  wheel.root.classList.add('open');
  wheel.options.onSlowmoChange?.(0.2);
  renderWheel();
}

export function hideWeaponWheel(selectedIndex = wheel?.selectedIndex) {
  if (!wheel) return;
  wheel.visible = false;
  wheel.root.classList.remove('open');
  wheel.options.onSlowmoChange?.(1);
  if (Number.isInteger(selectedIndex)) wheel.options.onSelect?.(selectedIndex);
}

export function updateWeaponWheel(mouseAngle = wheel?.mouseAngle || 0) {
  if (!wheel) initWeaponWheel();
  wheel.mouseAngle = mouseAngle;
  const slots = getSlots();
  const count = Math.max(1, slots.length);
  const normalized = (mouseAngle + Math.PI * 2.5) % (Math.PI * 2);
  wheel.selectedIndex = Math.floor(normalized / (Math.PI * 2 / count)) % count;
  renderWheel();
}

function renderWheel() {
  if (!wheel?.root) return;
  const slots = getSlots();
  const count = Math.max(1, slots.length);
  wheel.root.innerHTML = [
    '<div class="wheel-core"><span>Weapon</span><strong>Wheel</strong></div>',
    ...slots.map((slot, i) => renderSector(slot, i, count)),
  ].join('');
}

function renderSector(slot, index, count) {
  const angle = (index / count) * Math.PI * 2 - Math.PI / 2;
  const x = Math.cos(angle) * 150;
  const y = Math.sin(angle) * 150;
  const selected = index === wheel.selectedIndex;
  const locked = !slot.unlocked;
  return `
    <button class="weapon-sector ${selected ? 'selected' : ''} ${locked ? 'locked' : ''}" style="--x:${x}px;--y:${y}px" type="button" data-index="${index}">
      <b>${weaponIcon(slot.id)}</b>
      <span>${escapeHtml(slot.name)}</span>
      <small>${locked ? 'Locked' : `${Math.round(slot.dps || 0)} DPS`}</small>
    </button>
  `;
}

function createWheel() {
  let root = document.getElementById('weaponWheel');
  if (root) return root;
  root = document.createElement('div');
  root.id = 'weaponWheel';
  document.body.appendChild(root);
  root.addEventListener('click', (event) => {
    const button = event.target.closest?.('.weapon-sector');
    if (!button) return;
    const index = Number(button.dataset.index);
    if (Number.isInteger(index)) {
      wheel.selectedIndex = clamp(index, 0, getSlots().length - 1);
      hideWeaponWheel(wheel.selectedIndex);
    }
  });
  injectStyles();
  return root;
}

function getSlots() {
  return wheel?.options.getSlots?.() || [];
}

function injectStyles() {
  if (document.getElementById('weaponWheelStyles')) return;
  const style = document.createElement('style');
  style.id = 'weaponWheelStyles';
  style.textContent = `
    #weaponWheel {
      pointer-events:none; position:fixed; inset:0; z-index:19; opacity:0; visibility:hidden;
      background:radial-gradient(circle at 50% 50%, rgba(5,8,14,.42), rgba(5,8,14,.12) 38%, transparent 62%);
      transition:opacity 120ms ease, visibility 120ms ease;
    }
    #weaponWheel.open { pointer-events:auto; opacity:1; visibility:visible; }
    .wheel-core {
      position:absolute; left:50%; top:50%; width:136px; height:136px; display:grid; place-items:center;
      transform:translate(-50%,-50%); border-radius:50%; border:1px solid rgba(83,255,181,.32);
      background:rgba(4,7,14,.82); box-shadow:0 0 44px rgba(83,255,181,.14); text-transform:uppercase;
    }
    .wheel-core span { color:#8391a3; font-size:11px; font-weight:900; letter-spacing:.12em; }
    .wheel-core strong { color:#eaf4ff; font-size:20px; line-height:1; letter-spacing:-.04em; }
    .weapon-sector {
      position:absolute; left:50%; top:50%; width:132px; min-height:94px; display:grid; gap:3px; place-items:center;
      transform:translate(calc(-50% + var(--x)), calc(-50% + var(--y)));
      border:1px solid rgba(255,255,255,.16); border-radius:8px; background:rgba(6,10,18,.82);
      color:#eaf4ff; font:800 12px/1.15 Inter,system-ui,sans-serif; text-align:center; cursor:pointer;
      box-shadow:0 18px 46px rgba(0,0,0,.32); transition:transform 100ms ease, border-color 100ms ease, filter 100ms ease;
    }
    .weapon-sector b { color:#53ffb5; font-size:19px; }
    .weapon-sector span { max-width:112px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
    .weapon-sector small { color:#8391a3; text-transform:uppercase; letter-spacing:.08em; }
    .weapon-sector.selected { border-color:#53ffb5; filter:drop-shadow(0 0 18px rgba(83,255,181,.34)); transform:translate(calc(-50% + var(--x)), calc(-50% + var(--y))) scale(1.08); }
    .weapon-sector.locked { opacity:.45; filter:saturate(.25); }
  `;
  document.head.appendChild(style);
}

function weaponIcon(id) {
  if (id === 'shotgun') return 'S12';
  if (id === 'rail') return 'RL';
  if (id === 'plasma') return 'NV';
  return 'SMG';
}

function escapeHtml(str) {
  return String(str).replace(/[&<>'"]/g, (char) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    "'": '&#39;',
    '"': '&quot;',
  }[char]));
}
